import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  Bug,
  Check,
  Lightbulb,
  MessageCircle,
  MessageSquare,
  X,
} from "lucide-react";
import { usePastConsentGate } from "@/components/consent/ConsentGate";
import { useSupport } from "@/contexts/SupportContext";
import { Button, IconButton } from "@/components/ui/button";
import { ChoiceCard } from "@/components/ui/ChoiceCard";
import { IconTile } from "@/components/ui/IconTile";
import { useDismissed } from "@/components/ui/dismiss";
import { tabStops } from "@/components/ui/tab-ring";
import { Panel } from "@/components/ui/Panel";
import type { FeedbackKind, SupportClient } from "@/lib/support/support-client";
import { FeedbackForm } from "./FeedbackForm";

const TITLE = "Share feedback on Praxis";

/** The prototype's words, its dashes in the placeholders written as a colon or a comma. */
const KINDS: readonly {
  kind: FeedbackKind;
  icon: ReactNode;
  title: string;
  description: string;
  placeholder: string;
}[] = [
  {
    kind: "bug",
    icon: <Bug />,
    title: "Report a bug",
    description: "Something is broken or behaving unexpectedly",
    placeholder:
      "Describe the bug in as much detail as you can: what you were attempting to do at the time, what you expected to happen, and what happened instead.",
  },
  {
    kind: "feature",
    icon: <Lightbulb />,
    title: "Suggest a new feature",
    description: "An idea for something Praxis should do",
    placeholder:
      "Describe your idea in as much detail as you can: what the feature would do, when you would use it, and the problem it would solve.",
  },
  {
    kind: "other",
    icon: <MessageSquare />,
    title: "Any other feedback",
    description: "Anything else you want the team to know",
    placeholder:
      "Share your feedback in as much detail as you can, including what you were attempting to do at the time.",
  },
];

type View =
  | { at: "menu"; from?: FeedbackKind }
  | { at: "form"; kind: FeedbackKind }
  | { at: "done" };

/**
 * The feedback pill at the right end of the header, and the popover it opens.
 *
 * Drawn only past the legal gate, for someone signed in who has accepted the
 * policies. The public policy pages wear the same header, and nothing typed
 * there may reach PostHog before consent. Support is not asked until then
 * either, so the SDK does not start while the session is still being restored.
 *
 * Past the gate it is always drawn, as the design draws it, whether or not
 * Support is on. Where it is not (a build with no project key, a project with
 * Support off, a browser that blocks PostHog), a send says so and the words
 * stay in the form.
 */
export function FeedbackButton() {
  const support = useSupport();
  return usePastConsentGate() ? <FeedbackPopover support={support} /> : null;
}

/**
 * A popover, not a modal, as `Menu` is: a press outside or Escape closes it.
 * What was written goes with it, because the flow inside is only mounted
 * while it is open.
 *
 * Unlike `Menu`, the design lays a clear scrim across the page under it, so
 * the press that closes it is not also a press on whatever was under the
 * pointer: a click away from a half-written form should not open a dialog or
 * leave the page.
 *
 * A form is `contained`, so a Tab too many does not throw the words away. A
 * send in flight holds it open, and holds the form's own ways out, so the
 * result always lands on the form that sent it: closed mid-send, a failure
 * would be lost with the words, and a success would still file the ticket.
 *
 * It is no taller than the window under the header and scrolls inside when it
 * would be. Fixed over a scrim, it cannot be brought into view by scrolling the
 * page, and at a high zoom the bug form runs past the window's foot.
 */
function FeedbackPopover({ support }: { support: SupportClient }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ at: "menu" });
  const [sending, setSending] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = useCallback(() => {
    if (!sending) setOpen(false);
  }, [sending]);
  useDismissed(open, panelRef, triggerRef, close, {
    held: sending,
    contained: view.at === "form",
    scrim: scrimRef,
  });

  // The control that closed it has left the document, so focus goes back to
  // the button, as Escape sends it.
  const finish = () => {
    close();
    triggerRef.current?.focus();
  };

  return (
    <>
      <Button
        ref={triggerRef}
        variant="accent"
        size="pill"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          if (open) {
            close();
            return;
          }
          setView({ at: "menu" });
          setOpen(true);
          // Asked on opening, so the answer is in by the time Confirm is
          // pressed and the send does not wait on PostHog's settings.
          void support.isAvailable();
        }}
      >
        <MessageCircle aria-hidden="true" />
        Share Feedback
      </Button>
      {open && (
        <div
          ref={scrimRef}
          aria-hidden="true"
          data-testid="feedback-scrim"
          className="fixed inset-0 z-50"
          onClick={close}
        />
      )}
      {open && (
        <Panel
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={TITLE}
          pad="none"
          lift="form"
          className="fixed top-[calc(var(--shell-header-h)+10px)] right-4 z-50 max-h-[calc(100dvh-var(--shell-header-h)-20px)] w-[470px] max-w-[calc(100vw-32px)] overflow-x-hidden overflow-y-auto"
        >
          <FeedbackFlow
            support={support}
            view={view}
            onView={setView}
            sending={sending}
            onSending={setSending}
            onClose={finish}
          />
        </Panel>
      )}
    </>
  );
}

/** The popover's views. The view is the popover's, since a form keeps the keyboard. */
function FeedbackFlow({
  support,
  view,
  onView: setView,
  sending,
  onSending,
  onClose,
}: {
  support: SupportClient;
  view: View;
  onView: (view: View) => void;
  sending: boolean;
  onSending: (sending: boolean) => void;
  onClose: () => void;
}) {
  const done = view.at === "done";

  let body: ReactNode;
  if (done) {
    body = <Head title={TITLE} onClose={onClose} />;
  } else if (view.at === "form") {
    const { kind } = view;
    const choice = KINDS.find((each) => each.kind === kind) ?? KINDS[0];
    const back = () => {
      setView({ at: "menu", from: kind });
    };
    // Keyed by the kind, so leaving it and choosing again starts it empty.
    body = (
      <>
        <Head
          title={choice.title}
          onBack={back}
          onClose={onClose}
          held={sending}
        />
        <FeedbackForm
          key={kind}
          kind={kind}
          placeholder={choice.placeholder}
          support={support}
          sending={sending}
          onSending={onSending}
          onCancel={back}
          onSent={() => {
            setView({ at: "done" });
          }}
        />
      </>
    );
  } else {
    body = (
      <Choices
        from={view.from}
        onChoose={(kind) => {
          setView({ at: "form", kind });
        }}
        onClose={onClose}
      />
    );
  }

  // The status line is mounted from the first view and holds nothing until a
  // send begins. A screen reader announces words arriving in a live region it
  // already knows, and may say nothing of one that arrives with them in it.
  // The wait is said to a reader alone: the button pressed says it to the eye.
  return (
    <>
      {body}
      <div role="status">
        {sending && <span className="sr-only">Sending your feedback</span>}
        {done && <Submitted />}
      </div>
      {done && <Done onClose={onClose} />}
    </>
  );
}

/**
 * The popover's heading line. Its icon buttons are the smallest the primitive
 * draws, a few pixels wider than the design's, so each leans into the padding
 * beside it: the arrow, the title and the cross sit where the design has them.
 */
function Head({
  title,
  onBack,
  onClose,
  held = false,
}: {
  title: string;
  onBack?: () => void;
  onClose: () => void;
  held?: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border py-2.5 pr-3.5 pl-4">
      {onBack !== undefined && (
        <IconButton
          variant="quiet"
          size="dense"
          aria-label="Back"
          className="-ml-0.5"
          disabled={held}
          onClick={onBack}
        >
          <ArrowLeft aria-hidden="true" />
        </IconButton>
      )}
      <h2 className="flex-1 text-[13.5px] font-bold tracking-[-0.01em] text-primary">
        {title}
      </h2>
      <IconButton
        variant="quiet"
        size="dense"
        aria-label="Close"
        className="-mr-0.5"
        disabled={held}
        onClick={onClose}
      >
        <X aria-hidden="true" />
      </IconButton>
    </div>
  );
}

/**
 * The three kinds. Focus lands on the first on opening, and on the one just
 * left on coming back, so the keyboard is never left on the page behind.
 */
function Choices({
  from,
  onChoose,
  onClose,
}: {
  from: FeedbackKind | undefined;
  onChoose: (kind: FeedbackKind) => void;
  onClose: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const at = Math.max(
      0,
      KINDS.findIndex(({ kind }) => kind === from),
    );
    tabStops(listRef.current)[at]?.focus();
  }, [from]);

  return (
    <>
      <Head title={TITLE} onClose={onClose} />
      <div ref={listRef} className="flex flex-col gap-2 px-4 pt-3.5 pb-4">
        {KINDS.map(({ kind, icon, title, description }) => (
          <ChoiceCard
            key={kind}
            icon={icon}
            title={title}
            description={description}
            onChoose={() => {
              onChoose(kind);
            }}
          />
        ))}
      </div>
    </>
  );
}

/** What a send that made a ticket says, inside the popover's status line. */
function Submitted() {
  return (
    <div className="flex flex-col items-center gap-2.5 px-[26px] pt-9 pb-3.5 text-center">
      <IconTile tone="success" shape="round" size="md">
        <Check />
      </IconTile>
      <p className="text-[13.5px] font-bold text-foreground">
        Feedback submitted
      </p>
      <p className="max-w-[300px] text-[12px] leading-[1.55] text-muted-foreground">
        Thank you. Your feedback has been sent to the Praxis team.
      </p>
    </div>
  );
}

/**
 * The way out once a ticket is made. It takes focus, since the form it
 * replaced took the keyboard with it, and a send held the keyboard in the
 * popover until then.
 */
function Done({ onClose }: { onClose: () => void }) {
  return (
    <div className="mt-3.5 flex justify-end px-4 pb-4">
      <Button autoFocus type="button" size="foot" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}
